import React, { useState, useEffect, useRef, useCallback } from 'react';
import useMobileExpand from '../hooks/useMobileExpand';

function AiAssistantWidget({ token }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [answer, setAnswer] = useState('');
  const [confidence, setConfidence] = useState('');
  const [sources, setSources] = useState([]);
  const [error, setError] = useState('');
  const { showLabel, handleTap } = useMobileExpand();

  // Draggable state
  const [pos, setPos] = useState({ x: null, y: null });
  const dragRef = useRef(null);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  useEffect(() => {
    setPos({ x: window.innerWidth - 165, y: window.innerHeight - 168 });
  }, []);

  const dragStartPos = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD = 5;

  const onMouseDown = (e) => {
    dragging.current = true;
    hasMoved.current = false;
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      if (!hasMoved.current && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      hasMoved.current = true;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 56, e.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 44, e.clientY - dragOffset.current.y)),
      });
    };
    const onMouseUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, []);

  const onTouchStart = (e) => {
    dragging.current = true;
    hasMoved.current = false;
    const touch = e.touches[0];
    const rect = dragRef.current.getBoundingClientRect();
    dragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    dragStartPos.current = { x: touch.clientX, y: touch.clientY };
  };

  useEffect(() => {
    const onTouchMove = (e) => {
      if (!dragging.current) return;
      const touch = e.touches[0];
      const dx = touch.clientX - dragStartPos.current.x;
      const dy = touch.clientY - dragStartPos.current.y;
      if (!hasMoved.current && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      hasMoved.current = true;
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 56, touch.clientX - dragOffset.current.x)),
        y: Math.max(0, Math.min(window.innerHeight - 44, touch.clientY - dragOffset.current.y)),
      });
    };
    const onTouchEnd = () => { dragging.current = false; };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    return () => { window.removeEventListener('touchmove', onTouchMove); window.removeEventListener('touchend', onTouchEnd); };
  }, []);

  const handleIconClick = () => {
    if (hasMoved.current) return;
    if (handleTap()) {
      setOpen(prev => !prev);
      if (!open) { setAnswer(''); setError(''); setConfidence(''); setSources([]); }
    }
  };

  const askAssistant = useCallback(async (text) => {
    const q = (text || query).trim();
    if (!q) return;
    setSending(true);
    setError('');
    setAnswer('');
    setConfidence('');
    setSources([]);
    try {
      const params = new URLSearchParams({ query: q });
      const res = await fetch(`/api/faq/answer?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed');
      setAnswer(String(data.answer || '').trim());
      setConfidence(String(data.confidence || '').trim());
      setSources(Array.isArray(data.sources) ? data.sources : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }, [query, token]);

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAssistant(); } };

  const quickChips = ['How to check-in a guest?', 'How to settle a bill?', 'How to create a reservation?', 'How to approve a PO?'];

  if (pos.x === null) return null;

  return (
    <>
      {/* ── Draggable Button ── */}
      <div
        ref={dragRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onClick={handleIconClick}
        style={{
          position: 'fixed', left: pos.x, top: pos.y, zIndex: 9997,
          height: 40, borderRadius: 20, paddingLeft: 12, paddingRight: showLabel ? 14 : 12, minWidth: showLabel ? 140 : 40, justifyContent: 'center',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          boxShadow: '0 4px 16px rgba(16,185,129,0.4)',
          display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          cursor: dragging.current ? 'grabbing' : 'grab',
          userSelect: 'none', transition: 'all 0.25s ease',
        }}
        title="AI Assistant"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        {showLabel && <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.03em' }}>AI Assistant</span>}
      </div>

      {/* ── Modal ── */}
      {open && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9996,
          background: 'rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          padding: window.innerWidth <= 600 ? 0 : '1.5rem',
        }} onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div style={{
            width: '100%', maxWidth: window.innerWidth <= 600 ? '100%' : 620,
            height: window.innerWidth <= 600 ? '100%' : undefined,
            maxHeight: window.innerWidth <= 600 ? '100%' : '85vh',
            background: '#fff', borderRadius: window.innerWidth <= 600 ? 0 : 16,
            boxShadow: '0 25px 80px rgba(0,0,0,0.25)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }} onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div style={{
              padding: '1rem 1.5rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #10b981, #059669)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>AI Assistant</div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem' }}>Ask operational questions about the system</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
                color: '#fff', cursor: 'pointer', padding: '6px 10px', fontSize: '1.1rem', lineHeight: 1,
              }}>{'\u2715'}</button>
            </div>

            {/* Input area */}
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 8 }}>What do you need help with?</div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder="e.g. How to check-in a walk-in guest?"
                  disabled={sending}
                  style={{
                    flex: 1, padding: '0.7rem 1rem', border: '2px solid #e2e8f0',
                    borderRadius: 10, fontSize: '0.95rem', outline: 'none',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#10b981'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; }}
                />
                <button onClick={() => askAssistant()} disabled={sending || !query.trim()}
                  style={{
                    padding: '0.7rem 1.5rem', borderRadius: 10, border: 'none',
                    background: sending || !query.trim() ? '#a7f3d0' : '#10b981',
                    color: '#fff', fontWeight: 700, cursor: sending ? 'wait' : 'pointer',
                    fontSize: '0.95rem',
                  }}>
                  {sending ? 'Asking...' : 'Ask'}
                </button>
              </div>

              {/* Quick chips */}
              <div style={{ marginTop: 10 }}>
                <span style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 500 }}>Try: </span>
                {quickChips.map(c => (
                  <button key={c} onClick={() => { setQuery(c); askAssistant(c); }} disabled={sending}
                    style={{
                      padding: '0.2rem 0.6rem', borderRadius: 14, border: '1px solid #e2e8f0',
                      background: '#f8fafc', color: '#10b981', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 600,
                      marginRight: 4, marginTop: 4,
                    }}
                    onMouseEnter={(e) => { e.target.style.background = '#10b981'; e.target.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.target.style.background = '#f8fafc'; e.target.style.color = '#10b981'; }}
                  >{c}</button>
                ))}
              </div>
            </div>

            {/* Answer area */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem', background: '#fafbfc' }}>
              {!answer && !error && !sending && (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem 0' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{'\uD83D\uDCAC'}</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>Ask anything about EASEPMS</div>
                  <div style={{ fontSize: '0.82rem', marginTop: 6 }}>How to create orders, settle bills, manage rooms, and more.</div>
                </div>
              )}

              {sending && (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: '#10b981' }}>
                  <div style={{ fontSize: '1rem', fontWeight: 600 }}>Thinking...</div>
                </div>
              )}

              {error && (
                <div style={{ padding: '0.8rem 1rem', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.88rem' }}>
                  {error}
                </div>
              )}

              {answer && (
                <div>
                  {/* AI Answer */}
                  <div style={{ padding: '1rem', borderRadius: 12, background: '#fff', border: '1px solid #d1fae5', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      </div>
                      <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#059669' }}>AI Assistant</span>
                      {confidence && (
                        <span style={{
                          marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                          background: confidence === 'HIGH' ? '#dcfce7' : confidence === 'MEDIUM' ? '#fef3c7' : '#fef2f2',
                          color: confidence === 'HIGH' ? '#166534' : confidence === 'MEDIUM' ? '#92400e' : '#991b1b',
                        }}>
                          {confidence}
                        </span>
                      )}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.7, color: '#1e293b' }}>{answer}</div>
                  </div>

                  {/* Sources */}
                  {sources.length > 0 && (
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Sources:</div>
                      {sources.map((s, i) => (
                        <div key={i} style={{ padding: '0.3rem 0', borderBottom: '1px solid #f3f4f6' }}>
                          {s.question || s.title || `Source ${i + 1}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AiAssistantWidget;
